import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { InsightCard } from '@/components/InsightCard';
import { MetricBasisChips } from '@/components/MetricBasisChips';
import { Karta } from '@/components/Karta';
import { Segmenty } from '@/components/Segmenty';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { SlupkiRankingowe } from '@/components/wykresy/Wykresy';
import { Portfolio } from '@/components/wykresy/Portfolio';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { DataQualityBadge, EmptyState, ErrorState, InsightOutcome, LoadingState } from '@/components/states';
import type { DataQuality } from '@/lib/dataQuality';
import { formatCount, formatDelta, formatGrosze, formatRange, formatRatio } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { bezWartosci, porzadek } from '@/lib/porzadekRankingu';
import { useDishSales, useRestaurantSales } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';
import type { DishSalesResponse, DishSalesRow } from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SPRZEDAŻ DAŃ — migracja do języka „SYGNAŁ" (kierunek C, R5-15-009)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Pytanie ekranu: CO KONKRETNIE SIĘ SPRZEDAJE — nie „ile łącznie".
 *
 * TOŻSAMOŚĆ DANIA. Wiersz jest kluczowany KANONICZNYM IDENTYFIKATOREM dania: to samo danie
 * sprzedane w piętnastu szkołach jest JEDNYM bytem z liczbą szkół w kolumnie, a nie piętnastoma
 * daniami. Bez tej zasady „szeroko sprzedawane danie" i „piętnaście słabych dań" wyglądają
 * w rankingu identycznie, a to są dwie przeciwne decyzje.
 *
 * PODSTAWA METRYKI PRZY LICZBIE, nie w przypisie: wartość liczona jest w cenach KATALOGOWYCH
 * (przed zniżką szkolną), wolumen to POZYCJE zamówienia, oś czasu to DZIEŃ DOSTAWY (ADR-003).
 */
export function DishSalesPage() {
  /*
   * OKNO POCHODZI Z ADRESU (useDateRange), tak jak na pulpicie i na ekranie insightów.
   * Wcześniej ten ekran brał `defaultRange()`, czyli sztywne 30 dni, i przekazywał je do haka —
   * a hak i tak scala okno z adresu. Efekt: ten sam endpoint /dish-sales zasilał pulpit oraz
   * insighty w oknie WYBRANYM, a tę tabelę w oknie DOMYŚLNYM. Te same insighty, ten sam byt,
   * dwie różne podstawy czasowe — dokładnie ta klasa rozjazdu, którą produkt ma wykluczać.
   * Zakres pobierania danych zostaje bez zmian: ten sam endpoint, ten sam `top_n`, ten sam filtr.
   */
  const [range] = useDateRange();

  // FILTR RESTAURACJI. Endpoint /dish-sales przyjmował `restaurant_id` od początku i honorował go
  // dla zakresu najemcy 'all' (crm/backend/routes/analytics.js, middleware/tenantScope.js) —
  // brakowało wyłącznie podłączenia po stronie ekranu. Analiza parytetu (O8) to wychwyciła.
  //
  // Lista restauracji pochodzi z ODPOWIEDZI BEZ FILTRA, nie z odfiltrowanego wyniku — inaczej
  // wybranie jednej restauracji zredukowałoby listę do tej jednej i nie dałoby się jej zmienić.
  const [restaurantId, setRestaurantId] = useState<number | ''>('');
  const wszystkie = useRestaurantSales();
  const { data, isLoading, error } = useDishSales({
    top_n: 25,
    ...(restaurantId === '' ? {} : { restaurant_id: restaurantId }),
  });

  const opcje = wszystkie.data?.restaurant_options ?? [];
  const nazwaRestauracji =
    restaurantId === ''
      ? 'wszystkie restauracje'
      : (opcje.find((o) => o.restaurant_id === restaurantId)?.restaurant_name ?? `#${restaurantId}`);

  const filtr = (
    <TextField
      select
      size="small"
      label="Restauracja"
      data-cc-card-id="DISHES-01"
      value={restaurantId}
      onChange={(e) => setRestaurantId(e.target.value === '' ? '' : Number(e.target.value))}
      sx={{ minWidth: 260 }}
    >
      <MenuItem value="">Wszystkie restauracje</MenuItem>
      {opcje.map((o) => (
        <MenuItem key={o.restaurant_id} value={o.restaurant_id}>
          {o.restaurant_name ?? `#${o.restaurant_id}`}
          {o.city ? ` · ${o.city}` : ''}
        </MenuItem>
      ))}
    </TextField>
  );

  const okno = data ? formatRange(data.range.date_from, data.range.date_to) : formatRange(range.date_from, range.date_to);

  return (
    <Box>
      {/* Nagłówek z makiety zamiast własnego wiersza z rozpychaczem: ten ekran miał już
          właściwy UKŁAD, ale zbudowany lokalnie. Wspólny komponent znosi duplikat i sprawia,
          że zmiana nagłówka nie omija jednego ekranu przez przeoczenie. */}
      <NaglowekEkranu
        tytul="Sprzedaż dań"
        opis={
          <>
            Na jakie pytanie odpowiada ten ekran: <strong>co konkretnie się sprzedaje</strong> — które danie,
            w ilu szkołach i jak zmieniło się wobec poprzedniego, równie długiego okresu.
          </>
        }
        akcja={
          <Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* Filtr zostaje widoczny także przy pustym wyniku — inaczej wybranie restauracji
                bez sprzedaży byłoby ślepą uliczką bez możliwości cofnięcia wyboru. */}
            {filtr}
            <DateRangeFilter wariant="naglowek" comparison={data?.range.comparison} />
          </Stack>
        }
      />

      {/* Podstawa metryki stoi PRZY oknie, nie w przypisie: okno tnie po dniu DOSTAWY, a nie po
          dniu zakupu, i bez tej informacji filtr dat znaczy co innego, niż się wydaje. */}
      <Box sx={{ mb: 2 }}>
        <MetricBasisChips basis={{ value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS', time: 'DELIVERY_DATE' }} />
      </Box>

      {isLoading ? <LoadingState /> : null}
      {error ? (
        <ErrorState
          error={error}
          context="Control Center czyta wyłącznie API. Gdy backend nie odpowiada albo sesja wygasła, nie pokazujemy liczb zastępczych."
        />
      ) : null}

      {/*
       * ZERO TO NIE TO SAMO CO PUSTO. Suma z pustego zbioru wynosi 0, więc odpowiedź bez ani
       * jednej pozycji jest w API nieodróżnialna od realnego zera sprzedaży. Nagłówek mówi więc
       * „brak danych", a nie „sprzedaż wyniosła 0" — i nie pokazujemy tu ani jednej wyzerowanej
       * kwoty, bo wyzerowana kwota czyta się jak zmierzony wynik.
       */}
      {/*
        BRAK DANYCH NIE USUWA UKŁADU (R5-15-014, §8 zlecenia).

        Ten ekran zwijał się przy pustym źródle do samego komunikatu — znikało portfolio dań
        i ranking, czyli dwie rzeczy, które w makiecie NIOSĄ ten ekran. Zostają jako ramy.

        Ważne rozróżnienie, którego nie wolno zatrzeć: KATALOG dań stoi na realnych danych
        referencyjnych TEST, a SPRZEDAŻ dań jest pusta. Brak sprzedaży NIE znaczy, że danie
        wypada słabo — znaczy, że nie mamy czym mierzyć. Rama portfolio mówi to wprost zamiast
        wrzucać dania w ćwiartkę „najsłabsze".
      */}
      {!isLoading && !error && data && czyPustyZbior({ wierszy: data.summary.total_units, bytow: data.count }) ? (
        <Stack spacing={3} className="sygnal-kaskada">
          {/*
            Płyta stoi TAKŻE przy pustym zbiorze — makieta renderuje ją zawsze, a produkt
            wygaszał ją całkowicie (różnica wysokiej wagi #1 w macierzy dla tego ekranu).
            `wartosc={null}` sprawia, że płyta mówi „brak danych", a nie „0,00 zł": wyzerowana
            kwota czyta się jak zmierzony wynik, a to byłoby kłamstwo o pustym źródle.
          */}
          <PlytaGlowna
            kartaId="DISHES-26"
            etykieta="Wartość sprzedaży dań (ceny katalogowe)"
            wartosc={null}
            opis={
              <Typography variant="body2">
                wartość okresu w cenach katalogowych, przed zniżką szkolną — to nie jest kwota pobrana
              </Typography>
            }
            zrodlo="CRM /analytics/dish-sales"
            okno={okno}
          />

          <EmptyState
            title="Brak danych o sprzedaży dań w tym oknie"
            description={`Okno ${okno} · ${nazwaRestauracji}. Źródło nie zwróciło ani jednej pozycji zamówienia z rozpoznanym daniem. Nie wiadomo, czy nikt nic nie kupił, czy nie ma czego liczyć — dlatego nie pokazujemy tu zera.`}
          />

          {/* Ten sam byt inwentarza co portfolio w gałęzi wynikowej (DISHES-11), w stanie pustym.
              Gałęzie wykluczają się wzajemnie, więc adres nigdy nie jest w DOM dwukrotnie. */}
          <Karta kartaId="DISHES-11" nadtytul="Cały katalog naraz" tytul="Portfolio dań" poziom="h2">
            <Portfolio
              punkty={null}
              zrodlo="CRM /analytics/dish-sales"
              osie={{
                x: { etykieta: 'liczba porcji', niska: 'rzadko kupowane', wysoka: 'często kupowane' },
                y: { etykieta: 'wartość katalogowa', niska: 'tanie', wysoka: 'drogie' },
                rozmiar: 'udział w wartości okna',
              }}
              komunikatPusty="Rejestr sprzedaży nie zwrócił ani jednej pozycji, więc żadnego dania nie da się umieścić na mapie. To NIE znaczy, że dania wypadają słabo — znaczy, że nie ma czym ich zmierzyć."
            />
          </Karta>

          <Karta nadtytul="Porządek: liczba porcji" tytul="Dania wg liczby porcji" poziom="h3">
            <SlupkiRankingowe
              pozycje={null}
              format={(v) => formatCount(v)}
              komunikatPusty="Nie ma czego szeregować: w tym oknie żadne danie nie ma zapisanej sprzedaży."
            />
          </Karta>
        </Stack>
      ) : null}

      {!isLoading && !error && data && !czyPustyZbior({ wierszy: data.summary.total_units, bytow: data.count }) ? (
        <WynikSprzedazy data={data} nazwaRestauracji={nazwaRestauracji} />
      ) : null}
    </Box>
  );
}

/**
 * Wynik dla niepustego okna. Wydzielony, żeby dowód powstawał z TYCH SAMYCH pól, które zasilają
 * liczby na ekranie — warstwa prezentacji nie może być właścicielem liczb.
 */
/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PORZĄDEK LISTY DAŃ — sterowanie „Porządek" z makiety, komplet sześciu wymiarów
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * To JEDYNY ekran, na którym kontrakt niesie wszystkie wymiary, jakich chce makieta — i pierwszy,
 * na którym połowa z nich bywa NIEZNANA:
 *
 *   unit_price_grosze  null, gdy pozycje nie mają ceny katalogowej,
 *   repeat_ratio       null, gdy nie da się policzyć powrotności,
 *   units_delta_pct    null, gdy dania nie było w oknie porównawczym — a to NIE jest -100%.
 *
 * Dlatego wszystkie porządki idą przez `porzadek()`: brak wartości ląduje NA KOŃCU niezależnie
 * od kierunku i nie udaje ani najgorszego, ani najlepszego wyniku. Danie, którego nie było
 * miesiąc temu, nie jest daniem, które „spadło do zera".
 */
const PORZADKI_DAN = {
  wartosc: {
    etykieta: 'Wartość',
    opis: 'malejąco po wartości katalogowej w oknie',
    klucz: (r: DishSalesRow) => r.current.revenue_list_grosze,
    kierunek: 'malejaco' as const,
  },
  pozycje: {
    etykieta: 'Pozycje',
    opis: 'malejąco po liczbie pozycji zamówienia',
    klucz: (r: DishSalesRow) => r.current.units,
    kierunek: 'malejaco' as const,
  },
  zasieg: {
    etykieta: 'Zasięg',
    opis: 'malejąco po liczbie szkół, w których danie się sprzedało',
    klucz: (r: DishSalesRow) => r.current.schools,
    kierunek: 'malejaco' as const,
  },
  cena: {
    etykieta: 'Cena',
    opis: 'malejąco po średniej cenie katalogowej',
    klucz: (r: DishSalesRow) => r.current.unit_price_grosze,
    kierunek: 'malejaco' as const,
  },
  powroty: {
    etykieta: 'Powroty',
    opis: 'malejąco po udziale kupujących, którzy wrócili',
    klucz: (r: DishSalesRow) => r.repeat_ratio,
    kierunek: 'malejaco' as const,
  },
  trend: {
    etykieta: 'Trend',
    opis: 'rosnąco po zmianie liczby pozycji — największe spadki pierwsze',
    klucz: (r: DishSalesRow) => r.units_delta_pct,
    kierunek: 'rosnaco' as const,
  },
} as const;

type KluczPorzadkuDan = keyof typeof PORZADKI_DAN;

function WynikSprzedazy({ data, nazwaRestauracji }: { data: DishSalesResponse; nazwaRestauracji: string }) {
  const okno = formatRange(data.range.date_from, data.range.date_to);
  const [porzadekDan, setPorzadekDan] = useState<KluczPorzadkuDan>('wartosc');
  const wybranyPorzadekDan = PORZADKI_DAN[porzadekDan];

  const daniaUszeregowane = useMemo(
    () =>
      [...data.dishes].sort(
        porzadek<DishSalesRow>(wybranyPorzadekDan.klucz, wybranyPorzadekDan.kierunek, (a, b) =>
          (a.meal_name ?? '').localeCompare(b.meal_name ?? '', 'pl'),
        ),
      ),
    [data.dishes, wybranyPorzadekDan],
  );

  const danBezWartosciPorzadku = bezWartosci(data.dishes, wybranyPorzadekDan.klucz);
  const oknoPorownawcze = formatRange(data.range.comparison.date_from, data.range.comparison.date_to);

  const ziarno = { wierszy: data.summary.total_units, bytow: data.count };
  const wartoscKatalogowa = wartoscDoPokazania(data.summary.total_revenue_list_grosze, ziarno);

  // Średnia cena pozycji: dzielenie tylko wtedy, gdy jest przez co dzielić. Zero pozycji daje
  // „brak danych", nigdy 0,00 zł — brak to niewiedza, nie zmierzona darmowość.
  const srCenaPozycji =
    data.summary.total_units > 0
      ? Math.round(data.summary.total_revenue_list_grosze / data.summary.total_units)
      : null;

  const zPoliczalnaZmiana = useMemo(
    () => data.dishes.filter((d) => d.units_delta_pct !== null).length,
    [data.dishes],
  );

  const dowodWartosci: Dowod = {
    tytul: 'Wartość sprzedaży dań (ceny katalogowe)',
    etykietaWartosci: 'Wartość wg cennika',
    wartosc: wartoscKatalogowa === null ? null : formatGrosze(wartoscKatalogowa),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_ITEM_ROWS', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/dish-sales → crm_order_items × crm_transactions (zamówienia zdeduplikowane przed złączeniem)',
    // Świeżość źródła sprzedaży jest w całym programie NIEUSTALONA. Wpisanie tu stempla odczytu
    // byłoby dokładnie tym błędem, który ekran „Jakość danych" nazywa największą otwartą luką.
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      'Σ (cena katalogowa pozycji) po ŻYWYCH pozycjach zamówień, których zamówienie ma dostawę w oknie. Średnia cena pozycji = ta suma ÷ liczba pozycji.',
    szczegoly: [
      ['Dania ze sprzedażą', formatCount(data.count)],
      ['Pozycje zamówień', formatCount(data.summary.total_units)],
      ['Średnia cena pozycji', srCenaPozycji === null ? 'brak danych' : formatGrosze(srCenaPozycji)],
      ['Pokazane w tabeli', formatCount(data.returned)],
      ['Dni w oknie', formatCount(data.range.days)],
      ['Filtr restauracji', nazwaRestauracji],
      ['Silnik', data.engine_version],
    ],
    proba: `${formatCount(data.summary.total_units)} pozycji zamówień`,
    ograniczenia: [
      'Suma obejmuje WSZYSTKIE dania z okna, także te spoza tabeli — tabela pokazuje wyłącznie czoło rankingu.',
      'Pozycje bez rozpoznanego identyfikatora dania są pomijane, więc ta suma z definicji nie uzgodni się z sumą transakcyjną ani z sumą wg szkół. To jest różnica DEFINICJI, nie rozjazd danych.',
      ...data.caveats,
    ],
    czegoNieWynika:
      'To NIE jest przychód. Wartość wg cennika liczy ceny przed zniżką szkolną, a kwota faktycznie pobrana nie jest lustrzana na ziarnie pozycji — dla części transakcji w ogóle nie istnieje w źródle. Nie wynika z tego również, ile pieniędzy zostało w firmie: zwroty księgują się osobną transakcją i tej sumy nie pomniejszają.',
  };

  const dowodWiersza: Dowod = {
    tytul: 'Wiersz dania: tożsamość, pozycje, szkoły',
    etykietaWartosci: 'Dania ze sprzedażą w oknie',
    wartosc: formatCount(data.count),
    podstawa: { volume: 'ORDER_ITEM_ROWS', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/dish-sales → agregat po kanonicznym identyfikatorze dania',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      'Grupowanie po KANONICZNYM identyfikatorze dania. Kolumna „Szkoły" to liczba różnych szkół z transakcji w oknie — to samo danie sprzedane w piętnastu szkołach daje JEDEN wiersz z liczbą 15, a nie piętnaście wierszy.',
    szczegoly: [
      ['Ziarno', 'pozycja zamówienia'],
      ['Tożsamość wiersza', 'kanoniczny identyfikator dania'],
      ['Szkoła', 'z TRANSAKCJI (stan z dnia zakupu), nie z bieżącej szkoły ucznia'],
      ['Nazwa dania', 'najświeższa z pozycji w oknie — katalog mógł ją później zmienić'],
      ['Udział', 'wartość dania ÷ wartość WSZYSTKICH dań w oknie (także spoza tabeli) — udziały sumują się do 100%'],
      ['Powroty', 'kupujący z co najmniej dwoma zamówieniami tego dania ÷ kupujący w oknie bieżącym'],
      ['Pozycje w oknie', formatCount(data.summary.total_units)],
      ['Pokazane wiersze', `${formatCount(data.returned)} z ${formatCount(data.count)}`],
    ],
    proba: `${formatCount(data.count)} dań`,
    ograniczenia: [
      'Liczba pozycji to liczba wierszy zamówienia. System główny rozbija wybór wielu sztuk na osobne wiersze, więc suma ilości służy wyłącznie kontroli spójności i jej rozjazd obniża jakość danych wiersza.',
    ],
    czegoNieWynika:
      'Kolumna „Szkoły" nie mówi, w ilu szkołach danie było DOSTĘPNE — tylko w ilu się sprzedało. Szkoła, która miała danie w karcie i nikt go nie kupił, jest tu nieodróżnialna od szkoły, która tego dania w ogóle nie miała: ten zbiór nie zna dostępności, zna wyłącznie zakupy.',
  };

  const dowodZmiany: Dowod = {
    tytul: 'Zmiana wobec okna porównawczego',
    etykietaWartosci: 'Wiersze z policzalną zmianą (w tabeli)',
    wartosc: `${formatCount(zPoliczalnaZmiana)} / ${formatCount(data.returned)}`,
    podstawa: { volume: 'ORDER_ITEM_ROWS', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/dish-sales → agregaty obu okien w jednym zapytaniu',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: 'NIEUSTALONA — źródło nie raportuje momentu odczytu',
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      'Δ% = (okno bieżące − okno porównawcze) ÷ okno porównawcze, liczone osobno dla pozycji i dla średniej ceny. Okno porównawcze jest równie długie i bezpośrednio poprzedzające — nie jest konfigurowalne.',
    szczegoly: [
      ['Dni w oknie', formatCount(data.range.days)],
      ['Wiersze w tabeli', formatCount(data.returned)],
      ['Bez porównania', formatCount(data.returned - zPoliczalnaZmiana)],
    ],
    proba: `${formatCount(data.returned)} wierszy w tabeli`,
    ograniczenia: [
      'Danie, którego nie było w oknie porównawczym, nie ma z czym się porównać — pokazujemy „—", nigdy 0%.',
      'Zmiana ceny wymaga policzalnej średniej w OBU oknach; brak pozycji w którymkolwiek z nich również daje „—".',
      'Oba okna są równie długie, ale nie kalendarzowo równoważne: dni wolne od nauki wpadają w nie nierównomiernie, a ten ekran nie normalizuje wyniku po dniach dostawowych.',
    ],
    czegoNieWynika:
      'Zmiana nie orzeka PRZYCZYNY: równoczesny ruch ceny i wolumenu jest współwystępowaniem, nie dowodem, że jedno wywołało drugie. Nie wynika z niej też, że danie zniknęło z oferty — brak w oknie porównawczym znaczy tyle samo „nie było w karcie", co „nikt nie kupił".',
  };

  return (
    <Stack spacing={3} className="sygnal-kaskada">
      <PlytaGlowna
        kartaId="DISHES-04"
        etykieta="Wartość sprzedaży dań (ceny katalogowe)"
        wartosc={wartoscKatalogowa === null ? null : formatGrosze(wartoscKatalogowa)}
        opis={
          <Typography variant="body2">
            wartość okresu w cenach katalogowych, przed zniżką szkolną — to nie jest kwota pobrana ·{' '}
            {nazwaRestauracji}
          </Typography>
        }
        zrodlo="CRM /analytics/dish-sales → crm_order_items, dostawa z transakcji"
        okno={okno}
        dowod={dowodWartosci}
        dowodId="DISHES-09"
        stopka={
          <Button
            size="small"
            component={Link}
            to="/restauracje/katalog"
            sx={{ color: 'var(--tekst-na-ciemnym)' }}
          >
            co jest w ofercie, a się nie sprzedaje →
          </Button>
        }
        boczne={
          <>
            <MiniMetryka kartaId="DISHES-05" wartosc={formatCount(data.count)} etykieta="dań ze sprzedażą" />
            <MiniMetryka kartaId="DISHES-06" wartosc={formatCount(data.summary.total_units)} etykieta="pozycji zamówień" />
            <MiniMetryka
              kartaId="DISHES-07"
              wartosc={srCenaPozycji === null ? null : formatGrosze(srCenaPozycji)}
              etykieta="śr. cena pozycji"
            />
            <MiniMetryka wartosc={formatCount(data.range.days)} etykieta="dni w oknie" />
          </>
        }
      />

      {/* Zastrzeżenia przychodzą Z API i są renderowane dosłownie. Skracanie ich w warstwie
          prezentacji zamieniłoby deklarację źródła w jej streszczenie. */}
      <Alert severity="info">
        <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
          {data.caveats.map((c) => (
            <Typography key={c} component="li" variant="body2">
              {c}
            </Typography>
          ))}
        </Stack>
      </Alert>

      {/*
        PORTFOLIO DAŃ — element, którym makieta NIESIE ten ekran („cały katalog naraz").
        Produkt miał go dotąd tylko w gałęzi pustej, czyli dokładnie tam, gdzie nie ma czego
        pokazać, a nie miał go tam, gdzie dane są.

        Osie są DOKŁADNIE te, które makieta opisuje w podpisie, i wszystkie trzy wielkości
        pochodzą z odpowiedzi API — żadna nie jest szacowana:
          • poziomo  `current.schools`                  — w ilu szkołach danie ma sprzedaż,
          • pionowo  `current.units / current.schools`  — ile porcji przypada na jedną taką szkołę,
          • wielkość `current.revenue_list_grosze`      — wartość okna w cenach katalogowych.

        Dania bez szkół w oknie dostają `y = null` zamiast zera: zero porcji na szkołę i BRAK
        szkoły to dwie różne rzeczy, a bańka w lewym dolnym rogu wyglądałaby jak zmierzony,
        słaby wynik. Portfolio takie punkty pomija i LICZY je w podpisie.
      */}
      <Karta
        kartaId="DISHES-11"
        nadtytul="Cały katalog naraz"
        tytul="Portfolio dań — zasięg × intensywność"
        poziom="h2"
      >
        <Portfolio
          punkty={data.dishes.map((r, i) => {
            const szkoly = r.current.schools;
            return {
              id: r.meal_id === null ? `bez-id-${i}` : String(r.meal_id),
              nazwa: r.meal_name ?? 'danie bez nazwy',
              x: r.meal_id === null ? null : szkoly,
              y: r.meal_id === null || szkoly === 0 ? null : r.current.units / szkoly,
              rozmiar: r.current.revenue_list_grosze,
              seria: i % 7,
            };
          })}
          zrodlo="CRM /analytics/dish-sales"
          osie={{
            x: { etykieta: 'szkoły ze sprzedażą', niska: 'wąski zasięg', wysoka: 'szeroki zasięg' },
            y: { etykieta: 'porcje na szkołę', niska: 'rzadko kupowane', wysoka: 'często kupowane' },
            rozmiar: 'wartość katalogowa w oknie',
          }}
        />
      </Karta>

      <Karta kartaId="DISHES-13" nadtytul="Sygnały z tego okna" tytul="Co z tego wynika" poziom="h3">
        {data.insights.length === 0 ? (
          // „Brak sygnału" i „brak danych" to dwie różne odpowiedzi — patrz InsightOutcome.
          <InsightOutcome
            analysed={data.count}
            window={{ date_from: data.range.date_from, date_to: data.range.date_to }}
            subject="dań"
          />
        ) : (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
            {data.insights.map((insight) => (
              <InsightCard key={insight.insight_id} insight={insight} />
            ))}
          </Box>
        )}
      </Karta>

      <Karta
        nadtytul="Jedno danie = jeden wiersz"
        tytul="Dania"
        poziom="h3"
        akcja={
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
              Porządek
            </Typography>
            <Segmenty
              etykieta="Porządek listy dań"
              wybrana={porzadekDan}
              onZmiana={(w) => setPorzadekDan(w)}
              opcje={Object.entries(PORZADKI_DAN).map(([k, v]) => ({
                wartosc: k as KluczPorzadkuDan,
                etykieta: v.etykieta,
              }))}
            />
            <PrzyciskDowodu kartaId="DISHES-24" dowod={dowodWiersza} etykieta="Czym jest wiersz" />
            <PrzyciskDowodu kartaId="DISHES-25" dowod={dowodZmiany} etykieta="Skąd zmiana" />
          </Stack>
        }
      >
        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mb: 1 }}>
          Jeden wiersz = jedno danie wg kanonicznego identyfikatora. To samo danie sprzedane w wielu
          szkołach jest tu JEDNYM bytem — kolumna „Szkoły" mówi, w ilu szkołach się sprzedało.
          Pokazane {formatCount(data.returned)} z {formatCount(data.count)} dań ze sprzedażą, okno {okno},
          porównanie do {oknoPorownawcze}.
        </Typography>

        <Typography variant="caption" sx={{ color: 'var(--tekst-slaby)', display: 'block', mb: 1.5, maxWidth: '86ch' }}>
          Kolejność: {wybranyPorzadekDan.opis}. Nadana na tym ekranie — backend zwraca dania wg
          własnego priorytetu.
          {danBezWartosciPorzadku > 0 ? (
            <>
              {' '}
              <strong>{formatCount(danBezWartosciPorzadku)}</strong> dań nie ma tej wielkości
              zmierzonej i stoi na końcu listy: to brak pomiaru, nie najsłabszy wynik. Cena bywa
              nieznana przy pozycjach bez ceny katalogowej, powrotność i trend — przy daniu, które
              nie występuje w oknie porównawczym.
            </>
          ) : null}
        </Typography>

        {/* Bez `component={Paper}`: tabela stoi już WEWNĄTRZ karty, więc druga rama dawała
            ramkę w ramce — dokładnie to, czego makieta unika (karta bez obramowania, sam cień). */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={`Sprzedaż dań — ${wybranyPorzadekDan.opis}`}>
            <TableHead>
              <TableRow>
                <TableCell>Danie</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-15">Wartość katalogowa</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-16">Udział</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-17">Pozycje</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-18">Zmiana pozycji</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-19">Śr. cena</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-20">Zmiana ceny</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-21">Powroty</TableCell>
                <TableCell align="right" data-cc-card-id="DISHES-22">Szkoły</TableCell>
                <TableCell data-cc-card-id="DISHES-23">Jakość</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Adres siada WYŁĄCZNIE na pierwszym wierszu. Karta inwentarza DISHES-14 to
                  „wiersz tabeli Dania" — jeden wzorzec, nie dwadzieścia pięć bytów. Adres
                  powielony na każdym wierszu wskazywał 25 elementów naraz, czyli nie wskazywał
                  żadnego: walidacja umie orzec „dokładnie jeden" i słusznie zgłaszała
                  niejednoznaczność. Jedna zaadresowana instancja dowodzi, że wiersze się
                  renderują — a o to w tej karcie chodzi. */}
              {daniaUszeregowane.map((row, i) => (
                <TableRow
                  key={row.meal_id ?? row.meal_name}
                  hover
                  data-cc-card-id={i === 0 ? 'DISHES-14' : undefined}
                >
                  <TableCell>{row.meal_name ?? `#${row.meal_id}`}</TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatGrosze(row.current.revenue_list_grosze)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {row.revenue_share_pct === null ? '—' : `${row.revenue_share_pct}%`}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatCount(row.current.units)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatDelta(row.units_delta_pct)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatGrosze(row.current.unit_price_grosze)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatDelta(row.unit_price_delta_pct)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatRatio(row.repeat_ratio)}
                  </TableCell>
                  <TableCell align="right" className="sygnal-liczba">
                    {formatCount(row.current.schools)}
                  </TableCell>
                  <TableCell>
                    <DataQualityBadge quality={row.data_quality as DataQuality} reason={row.data_quality_reason} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Karta>
    </Stack>
  );
}
